import { z } from "zod";

import { defineContractFromMeta } from "@shared/functions.ts";
import type {
  CodeRunnerErrorPayload,
  CodeRunnerResponsePayload,
  ConsoleLogEntry,
  JsonValue,
} from "@shared/code/types.ts";

const CONTRACT_NAME = "code-runner";

const CodeRunnerRequestSchema = z.object({
  codeId: z.string().uuid(),
  input: z.string(),
  sandbox: z.boolean().optional(),
});
export type CodeRunnerRequest = z.input<typeof CodeRunnerRequestSchema>;

const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ]),
);

const CodeRunnerErrorDetailsSchema: z.ZodType<CodeRunnerErrorPayload> = z
  .object({
    name: z.string().optional(),
    message: z.string(),
    stack: z.string().optional(),
    code: z.string().optional(),
  })
  .strict();

const ConsoleLogEntrySchema: z.ZodType<ConsoleLogEntry> = z
  .object({
    level: z.enum(["log", "info", "warn", "error"]),
    arguments: z.array(JsonValueSchema),
    timestamp: z.string(),
  })
  .strict();

const CodeRunnerResponseSchema: z.ZodType<CodeRunnerResponsePayload> = z
  .object({
    output: z.string().nullable(),
    error: CodeRunnerErrorDetailsSchema.nullable(),
    executionTime: z.number().nonnegative().nullable(),
    consoleLogs: z.array(ConsoleLogEntrySchema),
  })
  .strict();
export type CodeRunnerResponse = z.output<typeof CodeRunnerResponseSchema>;
export type CodeRunnerErrorDetails = z.output<
  typeof CodeRunnerErrorDetailsSchema
>;

const CodeRunnerContract = defineContractFromMeta({
  name: CONTRACT_NAME,
  meta: import.meta,
  input: CodeRunnerRequestSchema,
  output: CodeRunnerResponseSchema,
});

export default CodeRunnerContract;
export type CodeRunnerContract = typeof CodeRunnerContract;
export type CodeRunnerInput = z.input<typeof CodeRunnerRequestSchema>;
export type CodeRunnerOutput = z.output<typeof CodeRunnerResponseSchema>;
